import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { isRTL, getTextDirection, getRTLClasses, getDirAttribute } from '../../utils/rtl';
import { ContentLabels } from '../../types/navigation';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQProps {
  faqs: FAQItem[];
  language?: string;
  contentLabels?: ContentLabels;
}

const FAQItem = ({ faq, isRTL }: { faq: FAQItem; isRTL?: boolean }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Animation variants for the content
  const contentVariants = {
    hidden: { 
      opacity: 0,
      y: -10,
      height: 0,
      transition: { duration: 0.2, ease: [0.04, 0.62, 0.23, 0.98] }
    },
    visible: { 
      opacity: 1,
      y: 0,
      height: "auto",
      transition: { duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }
    }
  };

  return (
    <motion.div
      initial={false}
      className="group border-b border-gray-200 last:border-none"
      layout
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex w-full items-center justify-between py-6 text-left transition-colors group-hover:bg-gray-50/50 px-6 rounded-lg ${
          isRTL ? 'text-right' : 'text-left'
        }`}
        whileHover={{ scale: 1.002 }}
        whileTap={{ scale: 0.995 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        aria-expanded={isOpen}
      >
        <span className={`text-lg font-medium text-gray-900 ${isRTL ? 'pl-8' : 'pr-8'}`}>{faq.question}</span>
        <motion.div
          animate={{ 
            rotate: isOpen ? 180 : 0,
            background: isOpen ? 'rgb(249 250 251)' : 'rgba(255, 255, 255, 0)'
          }}
          transition={{ duration: 0.2 }}
          className={`${isRTL ? 'mr-4' : 'ml-4'} flex-shrink-0 rounded-full p-1`}
        >
          <ChevronDown className={`h-5 w-5 transition-colors ${isOpen ? 'text-gray-900' : 'text-gray-500'}`} />
        </motion.div>
      </motion.button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="overflow-hidden bg-gray-50/30"
          >
            <motion.div 
              className="px-6 pb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <p className={`text-base text-gray-600 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>{faq.answer}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default function FAQ({ faqs, language = "en", contentLabels }: FAQProps) {
  // Default fallback labels if contentLabels is not provided
  const labels = contentLabels || {
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    faqDescription: 'Find answers to common questions about our property and investment opportunities',
    // Other required fields with fallbacks
    availableUnitTypes: 'Available Unit Types',
    unitSize: 'Size:',
    startingFrom: 'Starting from',
    downloadBrochure: 'Download Brochure',
    contactExpert: 'Contact Expert',
    viewDetails: 'View Details',
    requestInfo: 'Request Information',
    bookTour: 'Book a Tour',
    whatsappAction: 'WhatsApp',
    callAction: 'Call Now',
    learnMore: 'Learn More',
    viewAll: 'View All',
    showMore: 'Show More',
    showLess: 'Show Less',
    paymentPlan: 'Payment Plan',
    downPayment: 'Down Payment',
    dueBy: 'Due by',
    noPaymentPlans: 'No payment plans available at this time.',
    primeLocation: 'Prime Location',
    nearbyPlaces: 'Nearby Places',
    keyDistances: 'Key Distances',
    noNearbyPlaces: 'No nearby places available.',
    noKeyDistances: 'No key distances available.',
    projectGallery: 'Project Gallery',
    closeGallery: 'Close gallery',
    dldPermitNumber: 'DLD Permit Number',
    scanQrCode: 'Scan QR Code for more information',
    fullName: 'Full Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email',
    phoneRequired: 'Phone number is required'
  };

  // RTL detection and classes  
  const isCurrentLanguageRTL = isRTL(language);
  const rtlClasses = getRTLClasses(language);
  const dirAttribute = getDirAttribute(language);

  return (
    <div className={`mx-auto max-w-4xl px-4 py-24 sm:px-6 lg:px-8 ${rtlClasses}`} dir={dirAttribute}>
      <div className="text-center mb-16">
        <motion.h2 
          className={`text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl ${rtlClasses}`}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {labels.frequentlyAskedQuestions}
        </motion.h2>
        <motion.p 
          className={`mt-6 text-lg text-gray-600 max-w-2xl mx-auto ${rtlClasses}`}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {labels.faqDescription}
        </motion.p>
      </div>
      <motion.div
        className="mt-12 space-y-1"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <FAQItem faq={faq} isRTL={isCurrentLanguageRTL} />
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
} 